import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Edit3, Eye, Calendar, Tag, CheckCircle2, Clock, Globe } from 'lucide-react';
import type { ArticleStatus } from '@zarin/types';
import { apiFetch } from '../lib/auth';

interface DisplayArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  contentHtml: string;
  authorName: string;
  coverImageUrl?: string | undefined;
  tags: string[];
  status: ArticleStatus;
  publishedAt?: string | undefined;
  createdAt: string;
}

const INITIAL_ARTICLES: DisplayArticle[] = [];

export default function JournalPage() {
  const [articles, setArticles] = useState<DisplayArticle[]>(INITIAL_ARTICLES);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    async function loadArticles() {
      try {
        setLoading(true);
        const res = await apiFetch<{ data: any[] }>('/articles');
        if (Array.isArray(res.data)) {
          setArticles(res.data.map((a: any) => ({
            id: a.id,
            slug: a.slug,
            title: a.title,
            excerpt: a.excerpt || '',
            contentHtml: a.content_html || a.contentHtml || '',
            authorName: a.author_name || a.authorName || 'Editorial Team',
            coverImageUrl: a.cover_image_url || a.coverImageUrl,
            tags: Array.isArray(a.tags) ? a.tags : (a.tags ? JSON.parse(a.tags) : []),
            status: a.status || 'draft',
            publishedAt: a.published_at || a.publishedAt,
            createdAt: a.created_at || a.createdAt || new Date().toISOString(),
          })));
        }
      } catch (err) {
        console.error('Failed to load articles:', err);
      } finally {
        setLoading(false);
      }
    }
    loadArticles();
  }, []);

  // Form states
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [author, setAuthor] = useState('Zarin Editorial');
  const [coverUrl, setCoverUrl] = useState('');
  const [tagsInput, setTagsInput] = useState('Fabric & Craft, Editorial');
  const [status, setStatus] = useState<ArticleStatus>('draft');

  const handleTitleChange = (val: string) => {
    setTitle(val);
    setSlug(val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const newArt: DisplayArticle = {
      id: `art_${Date.now()}`,
      slug: slug || `article-${Date.now()}`,
      title,
      excerpt,
      contentHtml: content,
      authorName: author,
      coverImageUrl: coverUrl.trim() || undefined,
      tags: tagsInput.split(',').map(t => t.trim()).filter(Boolean),
      status,
      publishedAt: status === 'published' ? new Date().toISOString().split('T')[0] : undefined,
      createdAt: new Date().toISOString(),
    };
    setArticles([newArt, ...articles]);
    setShowModal(false);
    setTitle('');
    setSlug('');
    setExcerpt('');
    setContent('');
    setCoverUrl('');
  };

  const togglePublish = (id: string) => {
    setArticles(prev => prev.map(a => {
      if (a.id !== id) return a;
      const nextStatus: ArticleStatus = a.status === 'published' ? 'draft' : 'published';
      return {
        ...a,
        status: nextStatus,
        publishedAt: nextStatus === 'published' ? new Date().toISOString().split('T')[0] : a.publishedAt,
      };
    }));
  };

  const filtered = articles.filter(a =>
    a.title.toLowerCase().includes(search.toLowerCase()) ||
    a.tags.some(t => t.toLowerCase().includes(search.toLowerCase())) ||
    a.authorName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Editorial Journal & Stories</h1>
          <p style={styles.subtitle}>Curate essays, craft deep dives, and garment care chronicles</p>
        </div>
        <button style={styles.btnPrimary} onClick={() => setShowModal(true)}>
          <Plus size={16} />
          <span>New Article</span>
        </button>
      </div>

      <div style={styles.list}>
        {filtered.length === 0 ? (
          <div style={{ padding: '60px 24px', textAlign: 'center', color: '#8A8A8E', background: '#1A1A1E', borderRadius: 8, border: '1px dashed #333' }}>
            <BookOpen size={36} color="#555" style={{ margin: '0 auto 12px' }} />
            <p style={{ margin: 0 }}>
              {loading ? 'Loading journal articles…' : 'No journal essays published yet. Click "New Article" above to create an editorial story.'}
            </p>
          </div>
        ) : (
          filtered.map((art) => (
            <div key={art.id} style={styles.articleCard}>
              {art.coverImageUrl && (
                <div style={styles.coverWrap}>
                  <img src={art.coverImageUrl} alt={art.title} style={styles.coverImg} />
                </div>
              )}
              <div style={styles.articleBody}>
                <div style={styles.topMeta}>
                  <span style={styles.statusBadge(art.status)}>{art.status.toUpperCase()}</span>
                  <span style={styles.authorMeta}>
                    By {art.authorName} • {art.publishedAt || 'Not published'}
                  </span>
                </div>

                <h3 style={styles.articleTitle}>{art.title}</h3>
                <p style={styles.articleExcerpt}>{art.excerpt}</p>

                <div style={styles.tagRow}>
                  {art.tags.map((t, idx) => (
                    <span key={idx} style={styles.tag}>#{t}</span>
                  ))}
                </div>

                <div style={styles.cardActions}>
                  <div style={styles.slugDisplay}>
                    <Globe size={13} color="#888" />
                    <code>/journal/{art.slug}</code>
                  </div>
                  <button
                    onClick={() => togglePublish(art.id)}
                    style={styles.publishToggle(art.status === 'published')}
                  >
                    {art.status === 'published' ? 'Unpublish to Draft' : 'Publish Article'}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Draft Editorial Article</h3>
            <p style={styles.modalSubtitle}>Compose rich essays and craftsmanship narratives for the storefront journal</p>

            <form onSubmit={handleCreate} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>Article Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  style={styles.input}
                  placeholder="e.g. In Praise of Unbleached Hemp"
                  required
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>URL Slug</label>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  style={styles.input}
                  placeholder="in-praise-of-unbleached-hemp"
                  required
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Summary Excerpt</label>
                <textarea
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  style={{ ...styles.input, height: 60, resize: 'none' }}
                  placeholder="1-2 sentences summarizing the essay..."
                  required
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Cover Image URL</label>
                <input
                  type="url"
                  value={coverUrl}
                  onChange={(e) => setCoverUrl(e.target.value)}
                  style={styles.input}
                  placeholder="https://images.unsplash.com/..."
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Article Content (HTML/Markdown)</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  style={{ ...styles.input, height: 120, resize: 'none' }}
                  placeholder="<p>Body text of the journal article...</p>"
                  required
                />
              </div>

              <div style={styles.rowFields}>
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label}>Tags (comma separated)</label>
                  <input
                    type="text"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={{ ...styles.field, width: 140 }}>
                  <label style={styles.label}>Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as ArticleStatus)}
                    style={styles.select}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="scheduled">Scheduled</option>
                  </select>
                </div>
              </div>

              <div style={styles.modalActions}>
                <button type="button" onClick={() => setShowModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitBtn}>
                  Save Article
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, any> = {
  page: {
    padding: '28px 32px',
    maxWidth: 1200,
    margin: '0 auto',
    color: '#E5E5E5',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 26,
    fontWeight: 600,
    color: '#FFFFFF',
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: '#8A8A8E',
    marginTop: 6,
  },
  btnPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#C9A84C',
    color: '#0A0A0A',
    border: 'none',
    padding: '9px 16px',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  articleCard: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 20,
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 8,
    overflow: 'hidden',
  },
  coverWrap: {
    flex: '1 1 220px',
    minHeight: 180,
    background: '#1E1E1E',
  },
  coverImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  articleBody: {
    flex: '2 1 280px',
    minWidth: 0,
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  topMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  statusBadge: (status: ArticleStatus) => ({
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.05em',
    padding: '2px 8px',
    borderRadius: 4,
    background: status === 'published' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(201, 168, 76, 0.15)',
    color: status === 'published' ? '#10B981' : '#C9A84C',
  }),
  authorMeta: {
    fontSize: 12,
    color: '#71717A',
  },
  articleTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 18,
    fontWeight: 600,
    color: '#FFF',
    margin: '0 0 8px 0',
  },
  articleExcerpt: {
    fontSize: 13,
    lineHeight: 1.6,
    color: '#A1A1AA',
    margin: '0 0 12px 0',
  },
  tagRow: {
    display: 'flex',
    gap: 6,
    marginBottom: 16,
  },
  tag: {
    fontSize: 11,
    color: '#C9A84C',
    background: 'rgba(201, 168, 76, 0.1)',
    padding: '2px 8px',
    borderRadius: 3,
  },
  cardActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    borderTop: '1px solid #1F1F1F',
    paddingTop: 12,
  },
  slugDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#8A8A8E',
  },
  publishToggle: (isPub: boolean) => ({
    background: isPub ? '#27272A' : '#10B981',
    color: isPub ? '#A1A1AA' : '#0A0A0A',
    fontWeight: 600,
    border: 'none',
    padding: '5px 12px',
    borderRadius: 4,
    fontSize: 12,
    cursor: 'pointer',
  }),
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#141414',
    border: '1px solid #2A2A2A',
    borderRadius: 10,
    width: 600,
    maxWidth: '92vw',
    padding: 24,
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 20,
    fontWeight: 600,
    color: '#FFF',
    margin: 0,
  },
  modalSubtitle: {
    fontSize: 12,
    color: '#8A8A8E',
    marginTop: 4,
    marginBottom: 20,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  rowFields: {
    display: 'flex',
    gap: 12,
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: '#A1A1AA',
  },
  input: {
    background: '#1F1F1F',
    border: '1px solid #333',
    borderRadius: 6,
    padding: '8px 12px',
    color: '#FFF',
    fontSize: 13,
    outline: 'none',
  },
  select: {
    background: '#1F1F1F',
    border: '1px solid #333',
    borderRadius: 6,
    padding: '8px 12px',
    color: '#FFF',
    fontSize: 13,
    outline: 'none',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 10,
  },
  cancelBtn: {
    background: '#27272A',
    border: 'none',
    borderRadius: 6,
    padding: '8px 16px',
    color: '#CCC',
    fontSize: 13,
    cursor: 'pointer',
  },
  submitBtn: {
    background: '#C9A84C',
    border: 'none',
    borderRadius: 6,
    padding: '8px 18px',
    color: '#0A0A0A',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
};
